export const LEGEND_SCROLL_THRESHOLD = 8;
export const MAX_LEGEND_HEIGHT = 120;
export const DEFAULT_TEMPORAL_CHART_HEIGHT = 288;
export const TOOLTIP_Z_INDEX = 1200;

const DEFAULT_FONT = "11px Arial, sans-serif";
const FALLBACK_CHARACTER_WIDTH = 6.25;

export const shouldRenderControlsPanel = ({
  contextFilterCount = 0,
  showViewBy = false,
  showMetricSelector = false,
} = {}) => Boolean(contextFilterCount || showViewBy || showMetricSelector);

export const shouldUseScrollableLegend = (seriesCount) =>
  Number(seriesCount) > LEGEND_SCROLL_THRESHOLD;

export const measureTextWidth = (value, font = DEFAULT_FONT) => {
  const text = String(value ?? "");
  if (
    typeof document !== "undefined" &&
    typeof CanvasRenderingContext2D !== "undefined"
  ) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
      context.font = font;
      return context.measureText(text).width;
    }
  }
  return text.length * FALLBACK_CHARACTER_WIDTH;
};

export const truncateLabelToWidth = ({
  label,
  maximumWidth,
  font = DEFAULT_FONT,
  measureText = measureTextWidth,
}) => {
  const fullLabel = String(label ?? "");
  const safeMaximum = Math.max(0, Number(maximumWidth) || 0);
  if (!fullLabel || measureText(fullLabel, font) <= safeMaximum) {
    return { displayLabel: fullLabel, fullLabel, truncated: false };
  }
  const ellipsis = "…";
  if (measureText(ellipsis, font) > safeMaximum) {
    return { displayLabel: ellipsis, fullLabel, truncated: true };
  }
  let low = 0;
  let high = fullLabel.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const candidate = fullLabel.slice(0, middle).trimEnd() + ellipsis;
    if (measureText(candidate, font) <= safeMaximum) low = middle;
    else high = middle - 1;
  }
  return {
    displayLabel: fullLabel.slice(0, low).trimEnd() + ellipsis,
    fullLabel,
    truncated: true,
  };
};

export const getCategoricalLayout = ({
  categoryCount,
  metricCount,
  orientation = "horizontal",
}) => {
  const categories = Math.max(1, Number(categoryCount) || 1);
  const metrics = Math.max(1, Number(metricCount) || 1);
  const profile = metrics === 1
    ? { rowHeight: 32, barSize: 17, barGap: 3, categoryGap: 12, minimumHeight: 236 }
    : metrics === 2
      ? { rowHeight: 48, barSize: 15, barGap: 3, categoryGap: 18, minimumHeight: 248 }
      : {
          rowHeight: 60 + Math.max(0, metrics - 3) * 9,
          barSize: 14,
          barGap: 3,
          categoryGap: 20,
          minimumHeight: 260,
        };
  const chartHeight = orientation === "vertical"
    ? 272
    : Math.max(profile.minimumHeight, categories * profile.rowHeight + 32);
  return { ...profile, chartHeight };
};

export const getRankingLayout = (categoryCount) => {
  const categories = Math.max(1, Number(categoryCount) || 1);
  const rowHeight = 34;
  return {
    rowHeight,
    barSize: 18,
    barGap: 3,
    categoryGap: 12,
    minimumHeight: 116,
    chartHeight: Math.max(116, categories * rowHeight + 30),
  };
};
export const getCategoryAxisLayout = ({
  labels,
  containerWidth = 720,
  font = DEFAULT_FONT,
  measureText = measureTextWidth,
}) => {
  const width = Math.max(280, Number(containerWidth) || 720);
  const maximum = Math.min(width < 520 ? 132 : 176, width * 0.35);
  const preferred = Math.max(
    72,
    ...(Array.isArray(labels) ? labels : []).map((label) => measureText(label, font) + 16),
  );
  const axisWidth = Math.round(Math.min(maximum, preferred));
  return {
    axisWidth,
    maximumWidth: Math.max(32, axisWidth - 14),
    bounded: preferred > maximum,
  };
};

export const getNumericAxisWidth = ({
  values,
  formatter,
  font = DEFAULT_FONT,
  measureText = measureTextWidth,
}) => {
  const formatted = [0, ...(Array.isArray(values) ? values : [])]
    .filter((value) => Number.isFinite(Number(value)))
    .map((value) => formatter(value));
  const widest = Math.max(0, ...formatted.map((value) => measureText(value, font)));
  return Math.max(38, Math.ceil(widest + 14));
};

export const getChartMargins = ({ chartType, orientation }) => {
  if (chartType === "temporal") return { top: 10, right: 26, left: 2, bottom: 10 };
  if (orientation === "horizontal") return { top: 8, right: 30, left: 2, bottom: 6 };
  return { top: 8, right: 20, left: 2, bottom: 28 };
};